import { useState } from "react";
import { Building2, MailPlus, MapPin } from "lucide-react";

import type { CoachEmploymentType } from "@flexv-coach/shared";

import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { useI18n } from "@/hooks/useI18n";
import { employmentLabel } from "@/lib/labels";
import {
    useAffiliationInvitations,
    useDecideInvitation,
} from "./useAffiliationInvitations";

/**
 * Menu Undangan — klub/merchant mengajukan afiliasi, COACH yang memutuskan.
 *
 * Kebalikan dari menu Afiliasi (read-only, diatur merchant): di sini coach
 * memegang keputusan untuk permintaan ber-jalur `coach`. Permintaan ber-jalur
 * `owner_company` diputuskan company pemilik, jadi tidak pernah muncul di sini.
 */
export function InvitationsPage() {
    const { t } = useI18n();
    const { data, isPending, isError, error, refetch } = useAffiliationInvitations();
    const decide = useDecideInvitation();
    const [pendingId, setPendingId] = useState<string | null>(null);

    const respond = (id: string, decision: "accept" | "decline") => {
        setPendingId(id);
        decide.mutate(
            { id, decision },
            {
                onSettled: () => setPendingId(null),
            },
        );
    };

    return (
        <div className="flex flex-col gap-6">
            <header>
                <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                    {t("invitations.title")}
                </h1>
                <p className="app-shell-text-muted mt-1 text-sm">
                    {t("invitations.subtitle")}
                </p>
            </header>

            <Alert tone="info">{t("invitations.decidedByYou")}</Alert>

            {decide.isError && (
                <Alert tone="error">
                    {decide.error instanceof Error
                        ? decide.error.message
                        : t("invitations.decideFailed")}
                </Alert>
            )}

            {isPending && <LoadingBlock label={t("invitations.loading")} />}

            {isError && (
                <Card>
                    <ErrorState
                        message={error instanceof Error ? error.message : t("invitations.loadFailed")}
                        onRetry={() => void refetch()}
                    />
                </Card>
            )}

            {data && data.length === 0 && (
                <Card>
                    <EmptyState
                        icon={<MailPlus className="h-6 w-6" />}
                        title={t("invitations.empty.title")}
                        description={t("invitations.empty.description")}
                    />
                </Card>
            )}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {data?.map((invitation) => (
                    <Card key={invitation.id} hover>
                        <CardHeader
                            title={invitation.company.name}
                            description={
                                invitation.outlet?.name ?? t("invitations.companyWide")
                            }
                            icon={<Building2 className="h-5 w-5" />}
                            action={<Badge tone="warning">{t("invitations.awaitingYou")}</Badge>}
                        />
                        <CardContent className="flex flex-col gap-4">
                            <dl className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <dt className="app-text-soft text-xs uppercase tracking-wide">
                                        {t("invitations.employmentType")}
                                    </dt>
                                    <dd className="app-text-primary font-semibold">
                                        {employmentLabel(
                                            invitation.employmentType as CoachEmploymentType,
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="app-text-soft text-xs uppercase tracking-wide">
                                        {t("invitations.requestedAt")}
                                    </dt>
                                    <dd className="app-text-primary font-semibold">
                                        {new Date(invitation.requestedAt).toLocaleDateString()}
                                    </dd>
                                </div>
                            </dl>

                            {invitation.outlet?.name && (
                                <p className="app-shell-text-muted flex items-center gap-2 text-sm">
                                    <MapPin className="h-4 w-4 shrink-0" />
                                    {invitation.outlet.name}
                                </p>
                            )}

                            <p className="app-shell-text-muted text-xs">
                                {t("invitations.firstClubNote")}
                            </p>

                            <div className="flex flex-wrap gap-2">
                                <Button
                                    size="sm"
                                    disabled={pendingId === invitation.id}
                                    onClick={() => respond(invitation.id, "accept")}
                                >
                                    {pendingId === invitation.id
                                        ? t("invitations.processing")
                                        : t("invitations.accept")}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={pendingId === invitation.id}
                                    onClick={() => respond(invitation.id, "decline")}
                                >
                                    {t("invitations.decline")}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
